import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, X, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' for front, 'environment' for back
  const [hasPermission, setHasPermission] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [facingMode]);

  const startCamera = async () => {
    try {
      // Stop existing stream if any
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      // Detect if mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      const constraints = {
        video: {
          facingMode: facingMode,
          // Use lower resolution on mobile to prevent crashes
          width: { ideal: isMobile ? 1280 : 1920 },
          height: { ideal: isMobile ? 720 : 1080 }
        }
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      setStream(mediaStream);
      setHasPermission(true);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Camera access error:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Camera permission denied. Please allow camera access.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else {
        toast.error('Failed to access camera: ' + error.message);
      }
      setHasPermission(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Detect if mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      // VERY small dimensions for mobile (600x600 max)
      const MAX_WIDTH = isMobile ? 600 : 1200;
      const MAX_HEIGHT = isMobile ? 600 : 1200;
      let width = video.videoWidth;
      let height = video.videoHeight;

      // Calculate new dimensions maintaining aspect ratio
      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }

      // Set canvas dimensions to resized dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw the video frame to canvas with new dimensions
      context.drawImage(video, 0, 0, width, height);

      // Very aggressive compression on mobile (0.3 quality)
      const quality = isMobile ? 0.3 : 0.7;
      const imageDataUrl = canvas.toDataURL('image/jpeg', quality);
      
      // Check size
      const sizeInKB = (imageDataUrl.length * 0.75) / 1024;
      console.log(`Captured image size: ${sizeInKB.toFixed(0)}KB`);
      
      if (sizeInKB > 500) {
        toast.warning('Image is large, may take time to upload');
      }

      // Pass the captured image to parent
      onCapture(imageDataUrl);
      
      // Increment photo count
      const newCount = photoCount + 1;
      setPhotoCount(newCount);
      
      toast.success(`Photo ${newCount} captured!`);
    } catch (error) {
      console.error('Capture error:', error);
      toast.error('Failed to capture photo. Try using file upload instead.');
    }
  };

  const handleDone = () => {
    stopCamera();
    if (photoCount > 0) {
      toast.success(`${photoCount} photo${photoCount > 1 ? 's' : ''} added to job!`);
    }
    onClose();
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl" data-testid="camera-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Take Photo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {hasPermission ? (
            <>
              {/* Video Preview */}
              <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="camera-video"
                />
              </div>

              {/* Hidden canvas for capturing */}
              <canvas ref={canvasRef} className="hidden" />

              {/* Camera Controls */}
              <div className="flex justify-center gap-4">
                <Button
                  data-testid="switch-camera-button"
                  onClick={switchCamera}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <RotateCw className="w-4 h-4" />
                  Switch Camera
                </Button>
                
                <Button
                  data-testid="capture-photo-button"
                  onClick={capturePhoto}
                  className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 px-8"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Take Photo {photoCount > 0 && `(${photoCount})`}
                </Button>

                <Button
                  data-testid="done-camera-button"
                  onClick={handleDone}
                  variant="outline"
                  className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                >
                  Done
                </Button>
              </div>
              
              {photoCount > 0 && (
                <p className="text-center text-sm text-green-600 font-medium">
                  {photoCount} photo{photoCount > 1 ? 's' : ''} captured! Take more or click Done.
                </p>
              )}
            </>
          ) : (
            <div className="py-12 text-center">
              <Camera className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 mb-4">Requesting camera access...</p>
              <p className="text-sm text-slate-500">
                Please allow camera permission in your browser
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CameraCapture;
